import { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { SettingModelSettingType } from 'api/models';
import { HubConnectionEvent } from 'app/modules/wallforming/components/content-container/types/hubConnectionEvent';
import {
    applyCurrentPoiPlacementVisibility,
    applyPrevPoiPlacementVisibility
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const getSolutionKey = (index: number) => `${index}`;

const executionCodes = new Set(['Enter', 'NumpadEnter', 'KeyE']);

const getArticleInstances = (articles: Record<string, string[]> | undefined) =>
    Object.fromEntries(Object.entries(articles ?? {}).map(([articleId, instanceIds]) => [articleId, [...instanceIds]]));

export const usePlacementSolutionHandler = () => {
    const appliedIdsRef = useRef<Record<string, string[]>>({});
    const selectedPoiIndexRef = useRef<number | undefined>(usePlacementStore((state) => state.poiSelection.current.index));
    const solutionItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    const {
        solutions,
        selectedSolution,
        showPlacementPreview,
        setSelectedSolution,
        setShowPlacementPreview,
        addPlacementIndicator,
        removeArticleInstances,
        setAppliedSolutionForPoi,
        resetHiddenInstanceIds
    } = usePlacementStore(
        useShallow((state) => ({
            solutions: state.placementSolutions,
            selectedSolution: state.selectedPlacementSolution,
            showPlacementPreview: state.showPlacementPreview,
            setSelectedSolution: state.setSelectedPlacementSolution,
            setShowPlacementPreview: state.setShowPlacementPreview,
            setAppliedSolutionForPoi: state.setAppliedCornerPlacementSolutionForPoi,
            addPlacementIndicator: state.addPlacementIndicator,
            removeArticleInstances: state.removeArticleInstances,
            resetHiddenInstanceIds: state.resetHiddenInstanceIds
        }))
    );

    const appliedKey = usePlacementStore((state) =>
        selectedPoiIndexRef.current === undefined
            ? null
            : (state.appliedCornerPlacementSolutionByPoi[selectedPoiIndexRef.current] ?? null)
    );

    useEffect(() => {
        const subscribed = usePlacementStore.subscribe(
            (state) => ({
                selectedSettingType: state.selectedSettingType,
                showPlacementPreview: state.showPlacementPreview,
                selectedSettingView: state.selectedSettingView
            }),
            () => {
                applyCurrentPoiPlacementVisibility(usePlacementStore.getState());
            }
        );

        return () => subscribed();
    }, []);

    useEffect(() => {
        const subscribedPoiSelection = usePlacementStore.subscribe(
            (state) => state.poiSelection.current.index,
            (newPoiIndex, prevPoiIndex) => {
                if (selectedPoiIndexRef.current !== newPoiIndex) {
                    selectedPoiIndexRef.current = newPoiIndex;
                    appliedIdsRef.current = {};
                    setSelectedSolution(null);
                    resetHiddenInstanceIds();

                    requestAnimationFrame(() => {
                        applyPrevPoiPlacementVisibility(prevPoiIndex, usePlacementStore.getState());
                        applyCurrentPoiPlacementVisibility(usePlacementStore.getState());
                    });

                    requestAnimationFrame(() => {
                        usePlacementStore.getState().updateArticleEdgesOverlay();
                    });
                }
            }
        );

        return () => subscribedPoiSelection();
    }, [resetHiddenInstanceIds, setSelectedSolution]);

    const handleOnApplySolution = useCallback(
        (index: number | null) => {
            if (solutions && index !== null && appliedKey !== getSolutionKey(index)) {
                const state = usePlacementStore.getState();
                const placement = solutions[index]?.placements ?? [];
                const selectedPoiIndex = state.poiSelection.current.index;
                const selectedPoi = selectedPoiIndex === undefined ? undefined : state.pois[selectedPoiIndex];
                const existingIds = getArticleInstances(selectedPoi?.articles);

                removeArticleInstances(existingIds, true);
                removeArticleInstances(appliedIdsRef.current, true);
                addPlacementIndicator(placement);
                appliedIdsRef.current = Object.fromEntries(
                    placement.map((group) => [group.articleGuid, group.instances.map((instance) => instance.id)])
                );
                if (state.hubConnection && state.sessionId) {
                    void state.hubConnection.send(HubConnectionEvent.PLACE_ARTICLE_GROUPS, state.sessionId, placement);
                }
                if (selectedPoiIndex !== undefined) {
                    setAppliedSolutionForPoi(selectedPoiIndex, getSolutionKey(index));
                }
            }
        },
        [addPlacementIndicator, appliedKey, removeArticleInstances, setAppliedSolutionForPoi, solutions]
    );

    const handleOnReset = useCallback(() => {
        if (selectedSolution === null) {
            return;
        }

        const state = usePlacementStore.getState();
        const selectedPoiIndex = state.poiSelection.current.index;
        if (selectedPoiIndex === undefined) {
            return;
        }

        const removals = getArticleInstances(state.pois[selectedPoiIndex]?.articles);

        state.removeArticleInstances(removals, true);
        state.removeArticleInstances(appliedIdsRef.current, true);
        appliedIdsRef.current = {};
        setSelectedSolution(null);
        state.setAppliedCornerPlacementSolutionForPoi(selectedPoiIndex, null);
    }, [selectedSolution, setSelectedSolution]);

    const handleOnDoubleClick = useCallback(
        (event: React.MouseEvent, index: number) => {
            setSelectedSolution(index);
            handleOnApplySolution(index);
            event.preventDefault();
        },
        [handleOnApplySolution, setSelectedSolution]
    );

    const handleOnPointerDown = useCallback(
        (index: number) => {
            setSelectedSolution(index);
        },
        [setSelectedSolution]
    );

    const handleOnKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (executionCodes.has(event.code)) {
                const state = usePlacementStore.getState();
                if (
                    state.selectedSettingView === SettingView.AUTOMATED_PLACEMENT &&
                    state.selectedSettingType === SettingModelSettingType.Corners
                ) {
                    handleOnApplySolution(selectedSolution);
                    event.preventDefault();
                }
            }
        },
        [handleOnApplySolution, selectedSolution]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleOnKeyDown);
        return () => window.removeEventListener('keydown', handleOnKeyDown);
    }, [handleOnKeyDown]);

    useEffect(() => {
        if (selectedSolution === null) {
            return;
        }

        solutionItemRefs.current.get(selectedSolution)?.scrollIntoView({ block: 'nearest' });
    }, [selectedSolution, solutions]);

    return {
        solutions,
        selectedSolution,
        showPlacementPreview,
        appliedKey,
        solutionItemRefs,
        handleOnApplySolution,
        handleOnReset,
        handleOnDoubleClick,
        handleOnPointerDown,
        setShowPlacementPreview
    };
};